import { useEffect, useRef } from 'react';
import { useLocation, useNavigationType } from 'react-router-dom';
import { consumeTabSwitchNav } from '../context/TabsContext';

const KEY = (path) => `bbc_scroll_${path}`;
const MAX_AGE_MS = 30 * 60 * 1000; // 30 min

/**
 * ScrollRestoration v4 — simple, correct, debuggable.
 *
 *  Key insight from v3 failure: the OLD effect's cleanup `save()` ran AFTER
 *  React mounted the new route.  At that point window.scrollY was already
 *  0 (new route's initial scroll), so cleanup overwrote the good
 *  per-route save with 0.  Result: every Back navigation got 0.
 *
 *  v4 fixes this by:
 *    • NEVER saving in the cleanup — the scroll-listener save is enough.
 *    • Saving aggressively on every scroll (rAF-throttled) and on every
 *      capture-phase document click (fires before React Router unmounts).
 *    • Restoring ONLY on POP (browser/keyboard/swipe Back).
 *    • PUSH / REPLACE always scroll to top (fresh navigation feel).
 *    • Re-applying restore at +50, +200, +500ms to cover async data loads.
 */
export function ScrollRestoration() {
  const location = useLocation();
  const navType = useNavigationType();
  const restoringRef = useRef(false);
  // True from the moment the user clicks a link/button until the route
  // change actually happens.  Blocks save() so a focus-induced scroll
  // on the new page can't clobber the value we just persisted.
  const navigatingRef = useRef(false);
  const lastPathnameRef = useRef(location.pathname);

  // Disable browser's automatic scroll restoration once
  useEffect(() => {
    if ('scrollRestoration' in window.history) {
      window.history.scrollRestoration = 'manual';
    }
  }, []);

  // Use pathname-only as save key.  This is critical: when navigating
  // from listing -> detail -> back, the listing's URL may have query
  // params (?page=2&filter=...).  If we keyed by full URL the saved
  // scroll position would still match on POP back — BUT pagination
  // clicks within the listing change the query and would create a
  // separate save key per page, sometimes orphaning the scroll history.
  // Pathname-only save is simpler and predictable: one save slot per
  // route, restored on POP back to that route.
  const saveKey = location.pathname;

  // --- SAVE EFFECT ---
  useEffect(() => {
    const myPath = saveKey; // pathname-only, see comment above

    let pendingY = null;
    // Latest user-driven scrollY, updated on every scroll event.  This
    // is our source of truth for "where was the user when they clicked
    // the link", regardless of any post-click layout shifts or
    // browser-initiated scroll anchoring.
    let lastY = window.scrollY || document.documentElement.scrollTop || 0;
    const save = (yOverride) => {
      if (restoringRef.current) return;
      if (navigatingRef.current) return; // locked between click and route-change
      // Prefer the tracked lastY over a fresh window.scrollY read —
      // window.scrollY can be momentarily wrong during a click → focus →
      // auto-scroll sequence.
      const y = yOverride != null ? yOverride : lastY;
      try {
        sessionStorage.setItem(KEY(myPath), JSON.stringify({ y, t: Date.now() }));
      } catch (_) { /* quota — ignore */ }
    };

    // ⚠️ DO NOT write to sessionStorage on every rAF — `setItem` +
    //    `JSON.stringify` are synchronous and on Android WebView each
    //    call costs 5–20 ms.  Doing it every frame during a momentum
    //    flick blocks the main thread so badly that scroll appears to
    //    "take forever".
    //
    //    Instead: keep the latest scrollY in `pendingY` cheaply (no
    //    storage access on the hot path) and flush it lazily after
    //    150 ms of idle, OR immediately on hide / click / unmount.
    let flushTimer = null;
    const scheduleFlush = () => {
      if (flushTimer) return;
      flushTimer = setTimeout(() => {
        flushTimer = null;
        if (pendingY != null) {
          save(pendingY);
          pendingY = null;
        }
      }, 150);
    };

    const onScroll = () => {
      const y = window.scrollY || document.documentElement.scrollTop || 0;
      lastY = y;
      pendingY = y;
      scheduleFlush();
    };

    // Capture-phase click: fires BEFORE React Router unmounts the page,
    // so we save the current scroll just before navigation. Most reliable.
    // We also engage `navigatingRef` so that any scroll event between
    // the click and the actual route-change (e.g., Application Review
    // mounting and triggering a focus-induced scroll) does NOT overwrite
    // the just-saved value.
    let releaseTimer = null;
    const flushNow = () => {
      if (flushTimer) { clearTimeout(flushTimer); flushTimer = null; }
      // This IS the authoritative "user just acted" save. It must NEVER
      // be blocked by navigatingRef from some EARLIER click on this same
      // page (a filter toggle, a Shadcn tab trigger, pagination) — write
      // directly, bypassing save()'s lock check entirely. Previously this
      // called save(), which itself starts with `if (navigatingRef.current)
      // return;` — so the very first non-navigating click on a page
      // permanently latched the lock (nothing else ever reset it, since
      // the only reset was the effect's cleanup, which only fires when
      // the pathname actually changes) and silently dropped every save
      // after that, including the real one at the moment of navigating
      // away. That is what made "click something inert, scroll, open a
      // detail page, Back" restore to 0 or a stale position.
      const y = pendingY != null ? pendingY : lastY;
      pendingY = null;
      if (!restoringRef.current) {
        try {
          sessionStorage.setItem(KEY(myPath), JSON.stringify({ y: Math.max(0, y), t: Date.now() }));
        } catch (_) { /* quota */ }
      }
      // Lock further saves until the route actually changes (cleanup
      // below resets it then) — but ALSO auto-release shortly after if
      // no navigation follows, so an in-page click that never navigates
      // can't freeze saving for the rest of the visit to this route.
      navigatingRef.current = true;
      clearTimeout(releaseTimer);
      releaseTimer = setTimeout(() => { navigatingRef.current = false; }, 500);
    };

    window.addEventListener('scroll', onScroll, { passive: true });
    document.addEventListener('click', flushNow, true);
    document.addEventListener('keydown', flushNow, true);
    window.addEventListener('pagehide', flushNow);
    window.addEventListener('beforeunload', flushNow);

    return () => {
      // CRITICAL: do NOT call save() here.  By the time React unmounts the
      // outgoing route, the new route is already mounted and window.scrollY
      // is 0 — so a save() here would clobber the good save with 0.
      window.removeEventListener('scroll', onScroll);
      document.removeEventListener('click', flushNow, true);
      document.removeEventListener('keydown', flushNow, true);
      window.removeEventListener('pagehide', flushNow);
      window.removeEventListener('beforeunload', flushNow);
      if (flushTimer) clearTimeout(flushTimer);
      if (releaseTimer) clearTimeout(releaseTimer);
      // Release the navigating lock — the new route is mounted, fresh
      // scroll-save can resume for the new saveKey.
      navigatingRef.current = false;
    };
  }, [saveKey]);

  // --- RESTORE EFFECT ---
  useEffect(() => {
    // If only the query string changed (pagination, filter, sort, or a
    // page's own "sync activeTab into ?tab=" effect firing a same-
    // pathname REPLACE right after mount) — keep the user where they
    // are. Treat as in-page state change, not a real navigation.
    //
    // This check is now belt-and-suspenders: the effect's deps array
    // below intentionally excludes `navType` so a same-pathname
    // REPLACE like that can never even RE-INVOKE this effect (and tear
    // down an in-flight restore's cleanup) in the first place — only a
    // genuine pathname change does. Previously `navType` WAS a dep, so
    // e.g. Customers.js syncing `?tab=` into the URL on mount (a no-op
    // REPLACE navigation, pathname unchanged) still re-ran this effect,
    // and its cleanup called finish() mid-restore — cancelling a POP
    // restore that had barely started.
    const pathnameChanged = lastPathnameRef.current !== location.pathname;
    lastPathnameRef.current = location.pathname;
    if (!pathnameChanged) {
      return;
    }

    // Switching between already-open staff tabs is NOT a "fresh page load" —
    // StaffTabKeepAliveShell already keeps that tab's own DOM mounted and
    // owns restoring ITS remembered scroll position. Forcing scrollTo(0,0)
    // here would fight that restore and always dump the user at the top.
    if (consumeTabSwitchNav()) {
      return;
    }

    if (navType === 'POP') {
      let parsed = null;
      try { parsed = JSON.parse(sessionStorage.getItem(KEY(saveKey)) || 'null'); } catch (_) {}
      const fresh = parsed && typeof parsed.y === 'number' && parsed.y > 0 &&
                    Date.now() - (parsed.t || 0) < MAX_AGE_MS;
      if (fresh) {
        const targetY = parsed.y;
        restoringRef.current = true;
        const startTime = Date.now();
        const timers = [];
        const rafs = [];
        let observer = null;
        let userOverride = false;
        let reached = false;
        let lastAppliedY = -1;
        // Tracks whether the page is still actively growing (paginated /
        // infinite-scroll lists that lazy-load more rows as the sentinel
        // comes into view after our own scrollTo jump). As long as it's
        // still growing we keep retrying well past the base 5s window —
        // only give up once growth has genuinely stalled.
        let lastHeight = document.documentElement.scrollHeight;
        let lastGrowthAt = Date.now();

        // Apply target scroll once.  Stops as soon as either:
        //   1. The target is reached AND the doc is tall enough
        //   2. The user manually scrolls (wheel / touch / keyboard)
        //   3. 5s elapses with no further DOM growth, or 15s absolute cap
        const finish = () => {
          if (observer) { observer.disconnect(); observer = null; }
          timers.forEach(clearTimeout);
          rafs.forEach(cancelAnimationFrame);
          // Hold the lock just long enough that the listing's own
          // post-load reflows don't immediately save a wrong y.
          setTimeout(() => { restoringRef.current = false; }, 200);
        };

        const apply = () => {
          if (userOverride || reached) return;
          window.scrollTo(0, targetY);
          const actual = window.scrollY || document.documentElement.scrollTop || 0;
          // Track what we ACTUALLY achieved (post-clamp), not the target
          // we wished for. A still-loading/short page clamps scrollTo to
          // its current max — comparing onScrollDelta against the wished
          // target instead of that clamped reality made every clamped
          // restore look like a user scroll and aborted it within 400ms.
          lastAppliedY = actual;
          const curHeight = document.documentElement.scrollHeight;
          if (curHeight > lastHeight) { lastHeight = curHeight; lastGrowthAt = Date.now(); }
          const docTallEnough = (curHeight - window.innerHeight) >= targetY - 2;
          const elapsed = Date.now() - startTime;
          const stalledMs = Date.now() - lastGrowthAt;

          if (Math.abs(actual - targetY) <= 2 && docTallEnough) {
            reached = true;
            finish();
            return;
          }
          // Keep retrying while within the base window, OR while the doc
          // is still actively growing (a slow infinite-scroll fetch) and
          // we haven't blown the absolute 15s ceiling.
          if (elapsed < 5000 || (stalledMs < 1500 && elapsed < 15000)) {
            rafs.push(window.requestAnimationFrame(apply));
          } else {
            // Growth has genuinely stalled and we're still short — the
            // page really is shorter than expected. Give up gracefully.
            finish();
          }
        };

        // Detect a real user-initiated scroll (wheel / touch / arrows) so
        // we stop fighting them.  We do NOT use the plain 'scroll' event
        // because our own scrollTo() triggers that.  Wheel / touchmove /
        // keydown are user-only.
        const handoff = () => {
          if (reached) return; // already done — no-op
          // TWA/Android fix: the hardware/edge-swipe Back gesture emits
          // residual `touchmove`/`wheel` events RIGHT as the destination
          // page mounts — before the user has actually scrolled anything.
          // Honouring those instantly aborted the restore and dumped the
          // user at the top.  Ignore override events fired within the first
          // 400ms; genuine user scrolls after that are still respected, and
          // `onScrollDelta` (position-based) catches real scrolls too.
          if (Date.now() - startTime < 400) return;
          userOverride = true;
          finish();
        };
        const onScrollDelta = () => {
          // If page jumps away from where WE just placed it (and we
          // haven't reached target yet), that's the user scrolling.
          const y = window.scrollY || document.documentElement.scrollTop || 0;
          if (!reached && lastAppliedY >= 0 && Math.abs(y - lastAppliedY) > 20) {
            handoff();
          }
        };
        window.addEventListener('wheel', handoff, { passive: true, capture: true });
        window.addEventListener('touchmove', handoff, { passive: true, capture: true });
        window.addEventListener('keydown', handoff, true);
        window.addEventListener('scroll', onScrollDelta, { passive: true });

        // Re-apply on DOM mutations ONLY until we've hit the target
        // once.  After that the observer disconnects (in finish()) so
        // it can't fight subsequent user scrolling.
        try {
          observer = new MutationObserver(() => {
            if (!reached && !userOverride) apply();
          });
          observer.observe(document.body, { childList: true, subtree: true });
        } catch (_) { /* old browser */ }

        // Hard ceiling: give up after 5 s no matter what.  Why so long?
        // Listing pages (Loans Hub, Payments, Reports) lazy-load their
        // rows after mount; if the user had scrolled deep into the list
        // before navigating away, the page needs every row in the DOM
        // before we can scrollTo() that deep position.  1.5 s was too
        // tight — on Android mobile data networks it routinely expires
        // before /api/loans?tab=declined returns its 100+ rows.
        timers.push(setTimeout(finish, 15000));

        apply();

        return () => {
          window.removeEventListener('wheel', handoff, { capture: true });
          window.removeEventListener('touchmove', handoff, { capture: true });
          window.removeEventListener('keydown', handoff, true);
          window.removeEventListener('scroll', onScrollDelta);
          finish();
        };
      }
    }
    // Fresh PUSH / REPLACE / no save → top of page
    window.scrollTo(0, 0);
    // Deliberately keyed on `saveKey` (pathname) ONLY — see the long
    // comment at the top of this effect for why `navType` must NOT be
    // a dependency here.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [saveKey]);

  return null;
}
